import React from "react";
import { Link } from "react-router-dom";

const CookiePolicy = () => {
  return (
    <div className="content">
      <h2>
        <Link to="/">Home</Link> / Cookie Policy
      </h2>
      <br />

      <p align="center">
        <b>ShareMy Cookie Policy</b>
      </p>
      <p>
        <b>Last Updated: July 21, 2026</b>
      </p>

      <p>
        <b>1. Overview</b>
      </p>
      <p>
        This Cookie Policy explains how ShareMy Inc. ("ShareMy," "we," "our," or
        "us") uses cookies and similar technologies when you visit our website
        or use our Services.
      </p>
      <p>
        Cookies help us operate the Services, remember your preferences, improve
        performance, understand how users interact with ShareMy, and enhance
        your overall experience.
      </p>
      <p>
        This Cookie Policy should be read together with our{" "}
        <Link to="/privacy-policy">Privacy Policy</Link>, which explains how we
        collect, use, disclose, and protect personal information.
      </p>

      <p>
        <b>2. What Are Cookies?</b>
      </p>
      <p>
        Cookies are small text files placed on your device when you visit a
        website.
      </p>
      <p>
        Cookies allow websites to recognize your browser or device and remember
        certain information about your visit.
      </p>
      <p>
        ShareMy may also use similar technologies such as local storage,
        software development kits (SDKs), pixels, tags, and similar technologies
        that perform comparable functions. For simplicity, this Policy refers to
        all of these technologies collectively as "cookies."
      </p>

      <p>
        <b>3. Why We Use Cookies</b>
      </p>
      <p>ShareMy uses cookies to:</p>
      <ul>
        <li>Authenticate users.</li>
        <li>Keep you signed in.</li>
        <li>Protect your account.</li>
        <li>Remember your preferences.</li>
        <li>Improve website performance.</li>
        <li>Understand how users discover activities.</li>
        <li>Analyze platform usage.</li>
        <li>Improve search and recommendations.</li>
        <li>Measure marketing effectiveness.</li>
        <li>Maintain the security and integrity of the Services.</li>
      </ul>
      <p>We do not use cookies to sell your personal information.</p>

      <p>
        <b>4. Types of Cookies We Use</b>
      </p>

      <p>
        <b>Essential Cookies (Always Active)</b>
      </p>
      <p>These cookies are necessary for the Services to function.</p>
      <p>They support:</p>
      <ul>
        <li>Authentication.</li>
        <li>Login sessions.</li>
        <li>Security.</li>
        <li>Fraud prevention.</li>
        <li>Load balancing.</li>
        <li>Basic platform functionality.</li>
      </ul>
      <p>
        Because these cookies are necessary to operate the Services, they cannot
        be disabled through our cookie preference center.
      </p>

      <p>
        <b>Functional Cookies</b>
      </p>
      <p>
        Functional cookies remember choices you make and personalize your
        experience.
      </p>
      <p>Examples include:</p>
      <ul>
        <li>Language preferences.</li>
        <li>Display settings.</li>
        <li>Notification preferences.</li>
        <li>Location preferences.</li>
        <li>Activity discovery settings.</li>
        <li>Accessibility preferences.</li>
      </ul>

      <p>
        <b>Analytics Cookies</b>
      </p>
      <p>Analytics cookies help us understand how people use the Services.</p>
      <p>These cookies help us:</p>
      <ul>
        <li>Improve performance.</li>
        <li>Identify errors.</li>
        <li>Understand feature usage.</li>
        <li>Measure engagement.</li>
        <li>Improve recommendations.</li>
      </ul>
      <p>Analytics information is generally collected in aggregated form.</p>

      <p>
        <b>Advertising and Marketing Cookies</b>
      </p>
      <p>
        Advertising cookies help us understand how users discover ShareMy and
        measure the effectiveness of our marketing efforts.
      </p>
      <p>These cookies may:</p>
      <ul>
        <li>Measure advertising campaigns.</li>
        <li>Track referrals.</li>
        <li>Help improve marketing performance.</li>
        <li>Limit repetitive advertisements.</li>
      </ul>
      <p>
        ShareMy does not permit third-party advertising networks to display
        behavioral advertisements within the ShareMy platform based on cookie
        data.
      </p>

      <p>
        <b>5. Technologies We Use</b>
      </p>
      <p>
        Depending on how you use the Services, ShareMy may use technologies
        provided by third-party service providers, including:
      </p>
      <ul>
        <li>Firebase (analytics and application performance).</li>
        <li>Google Maps (location-based features).</li>
        <li>Apple Sign-In.</li>
        <li>Google Sign-In.</li>
        <li>Sendbird (messaging).</li>
        <li>RevenueCat (subscription management).</li>
        <li>Stripe (website payment processing).</li>
      </ul>
      <p>
        These providers may place or access cookies or similar technologies as
        necessary to provide their services.
      </p>
      <p>
        Your use of those services is also governed by their respective privacy
        policies.
      </p>

      <p>
        <b>6. Your Cookie Choices</b>
      </p>
      <p>You may choose to:</p>
      <ul>
        <li>Accept all cookies.</li>
        <li>Reject non-essential cookies.</li>
        <li>Customize your cookie preferences.</li>
      </ul>
      <p>You may also manage cookies through your web browser.</p>
      <p>
        Disabling certain cookies may affect the functionality or availability
        of portions of the Services.
      </p>
      <p>
        You may change your cookie preferences at any time through our Cookie
        Settings panel.
      </p>

      <p>
        <b>7. California Privacy Rights</b>
      </p>
      <p>
        California residents may have additional rights under applicable privacy
        laws.
      </p>
      <p>
        Where required by law, ShareMy provides the ability to opt out of the
        sharing of personal information used for cross-context behavioral
        advertising.
      </p>
      <p>
        For additional information, please review our{" "}
        <Link to="/privacy-policy">Privacy Policy</Link> or use the "Do Not Sell
        or Share My Personal Information" link available on our website.
      </p>

      <p>
        <b>8. Changes to This Cookie Policy</b>
      </p>
      <p>
        We may update this Cookie Policy from time to time to reflect changes in
        technology, legal requirements, or our Services.
      </p>
      <p>
        When material changes are made, we will update the "Last Updated" date
        shown at the beginning of this Policy.
      </p>
      <p>
        Continued use of the Services after changes become effective constitutes
        acceptance of the updated Cookie Policy to the extent permitted by
        applicable law.
      </p>

      <p>
        <b>9. Contact Us</b>
      </p>
      <p>
        If you have questions about this Cookie Policy or your cookie
        preferences, please contact:
      </p>
      <p>ShareMy Inc.</p>
      <p>
        Email: <a href="mailto:support@sharemy.com">support@sharemy.com</a>
      </p>
      <p>
        Website: <a href="https://www.sharemy.com">www.sharemy.com</a>
      </p>
      <p>Mailing Address:</p>
      <p>
        ShareMy Inc.
        <br />
        6444 E Old Oregon Trail Rd.
        <br />
        Lava Hot Springs, Idaho, United States
      </p>
    </div>
  );
};

export default CookiePolicy;
